import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import type { ChangelogEntry } from '../../services/versionService';
import { compareSemver } from '../../utils/semver';

interface Props {
  open: boolean;
  onClose: () => void;
  entries: ChangelogEntry[];
  initialVersion?: string;
  isNewVersionDialog?: boolean;
}

export default function ChangelogDialog({ open, onClose, entries, initialVersion, isNewVersionDialog = false }: Props) {
  const sorted = [...entries].sort((a, b) => compareSemver(b.version, a.version));

  const [selectedVer, setSelectedVer] = useState(
    initialVersion && sorted.find(e => e.version === initialVersion) ? initialVersion : sorted[0]?.version || '',
  );

  const selected = sorted.find(e => e.version === selectedVer);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '60vh',
          bgcolor: '#0a0a0a',
          color: '#ffffff',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          color: '#ffffff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isNewVersionDialog && <NewReleasesIcon sx={{ color: 'rgba(220, 38, 38, 0.8)' }} />}
          <Typography variant='h6'>{isNewVersionDialog ? "What's New!" : 'Changelog'}</Typography>
        </Box>
        <IconButton onClick={onClose} size='small' sx={{ color: '#ffffff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: '#333333' }} />

      <DialogContent sx={{ display: 'flex', height: '60vh', p: 0 }}>
        {/* Version List Sidebar */}
        <Box
          sx={{
            width: 220,
            overflowY: 'auto',
            borderRight: '1px solid',
            borderColor: '#333333',
            bgcolor: '#000000',
          }}
        >
          <List dense sx={{ p: 1 }}>
            {sorted.map(entry => (
              <ListItemButton
                key={entry.version}
                selected={entry.version === selectedVer}
                onClick={() => setSelectedVer(entry.version)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: 'rgba(220, 38, 38, 0.1)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(220, 38, 38, 0.2)',
                    color: '#ffffff',
                    border: '1px solid rgba(220, 38, 38, 0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(220, 38, 38, 0.3)',
                    },
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='subtitle2' component='span'>
                        v{entry.version}
                      </Typography>
                      {isNewVersionDialog && (
                        <Chip
                          label='NEW'
                          size='small'
                          sx={{
                            height: 16,
                            fontSize: '0.6rem',
                            bgcolor: 'rgba(220, 38, 38, 0.8)',
                            color: '#ffffff',
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant='caption' sx={{ color: '#cccccc' }}>
                      {entry.date || 'No date'}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Changelog Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: '#0a0a0a' }}>
          {selected ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant='h5' component='h2' gutterBottom sx={{ color: '#ffffff' }}>
                  Version {selected.version}
                </Typography>
                {selected.date && (
                  <Typography variant='body2' gutterBottom sx={{ color: '#cccccc' }}>
                    Released on {selected.date}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ borderColor: '#333333' }} />

              <Box>
                <Typography variant='h6' gutterBottom sx={{ color: '#ffffff' }}>
                  Changes:
                </Typography>
                <Box component='ul' sx={{ pl: 2, m: 0 }}>
                  {selected.changes.map((change, index) => (
                    <Typography component='li' key={index} variant='body2' sx={{ mb: 0.5, color: '#ffffff' }}>
                      {change}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant='body1' sx={{ color: '#cccccc' }}>
                No changelog selected
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
